import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Tracks which editor panel is open. Only one is ever open at a time —
 * opening any panel implicitly closes whatever was open before. A mousedown
 * outside the active panel's marked DOM (`[data-panel-id="<id>"]`) closes
 * it, so every panel needs to mark its own root and its trigger with the
 * same `data-panel-id` to opt in.
 */

export type PanelId =
  | 'layers'
  | 'background'
  | 'effects'
  | 'loadJson'
  | 'json'
  | 'shapes'
  | 'text'
  | 'upload'
  | 'sound'
  | 'sobreimpresoPresets';

type PanelsContextValue = {
  open: (id: PanelId) => void;
  close: () => void;
  toggle: (id: PanelId) => void;
  isOpen: (id: PanelId) => boolean;
};

const PanelsContext = createContext<PanelsContextValue | null>(null);

export const PanelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activePanel, setActivePanel] = useState<PanelId | null>(null);

  useEffect(() => {
    if (!activePanel) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(`[data-panel-id="${activePanel}"]`)) return;
      setActivePanel(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activePanel]);

  const value = useMemo<PanelsContextValue>(
    () => ({
      open: id => setActivePanel(id),
      close: () => setActivePanel(null),
      toggle: id => setActivePanel(current => (current === id ? null : id)),
      isOpen: id => activePanel === id
    }),
    [activePanel]
  );

  return <PanelsContext.Provider value={value}>{children}</PanelsContext.Provider>;
};

export const usePanels = (): PanelsContextValue => {
  const ctx = useContext(PanelsContext);
  if (!ctx) throw new Error('usePanels must be used within a PanelsProvider');
  return ctx;
};
